import type { ReactNode } from 'react';
import './partner-billing.css';

/** A fact: a label over its value, and a quieter note under it. */
export interface Fact {
  label: string;
  value: ReactNode;
  note?: ReactNode;
}

/** Facts in a grid that wraps to one column on a phone. */
export function FactList({ facts, spaced }: { facts: Fact[]; spaced?: boolean }): ReactNode {
  return (
    <dl className="pb-facts" style={spaced ? { marginTop: 'var(--paige-space-4)' } : undefined}>
      {facts.map((f) => (
        <div key={f.label}>
          <dt>{f.label}</dt>
          <dd>
            {f.value}
            {f.note ? <small>{f.note}</small> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** A state that changes what a company or account gets: paused, lapsed, moving, stopping. */
export function Banner(p: {
  tone?: 'warning' | 'danger';
  title: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className={`pb-banner${p.tone ? ` pb-banner--${p.tone}` : ''}`} role="status">
      <span className="pb-banner__dot" aria-hidden="true" />
      <div>
        <strong>{p.title}</strong>
        {p.children}
      </div>
    </div>
  );
}

/** A column heading; `num` right-aligns it over figures. */
export function Th({ children, num }: { children: ReactNode; num?: boolean }): ReactNode {
  return (
    <th scope="col" className={num ? 'is-num' : undefined}>
      {children}
    </th>
  );
}

/** A table in its own horizontal scroll, so a phone scrolls the table and never the page. */
export function ScrollTable(p: {
  head: ReactNode;
  children: ReactNode;
  foot?: ReactNode;
  label?: string;
}): ReactNode {
  return (
    <div className="pb-scroll">
      <table className="pb-table" aria-label={p.label}>
        <thead>
          <tr>{p.head}</tr>
        </thead>
        <tbody>{p.children}</tbody>
        {p.foot ? <tfoot>{p.foot}</tfoot> : null}
      </table>
    </div>
  );
}

/** Reading, nothing, or the rows. */
export function ListState(p: { rows: unknown[] | undefined; none: string }): ReactNode {
  if (!p.rows) return <p className="ad-small pui-muted">Reading…</p>;
  if (p.rows.length === 0) return <p className="ad-small pui-muted">{p.none}</p>;
  return null;
}
